import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  doc, 
  updateDoc,
  onSnapshot,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import employeeService from './employeeService';

/**
 * Admin Attendance Service
 * Handles admin-specific attendance operations including monitoring, filtering, and status updates
 */
class AdminAttendanceService {
  constructor() {
    this.employeeCache = new Map();
    this.warehouseLocations = [];
  }

  /**
   * Subscribe to attendance records with real-time updates
   * @param {Function} callback - Callback function to handle data updates
   * @param {Object} options - Filter options
   * @param {Date} options.startDate - Start date filter
   * @param {Date} options.endDate - End date filter
   * @param {string} options.employeeId - Employee ID filter
   * @param {string} options.status - Status filter (approved/pending/rejected)
   * @param {string} options.type - Type filter (clock-in/clock-out)
   * @param {string} options.location - Location filter (warehouse/offsite)
   * @param {number} options.limitCount - Maximum number of records to fetch
   * @returns {Function} Unsubscribe function
   */
  subscribeToAttendanceRecords(callback, options = {}) {
    try {
      console.log('🔄 Setting up attendance records subscription with options:', options);

      // Build query
      let attendanceQuery = collection(db, 'attendance');
      const queryConstraints = [];

      // Add date range filters
      if (options.startDate) {
        queryConstraints.push(where('timestamp', '>=', Timestamp.fromDate(options.startDate)));
      }
      if (options.endDate) {
        queryConstraints.push(where('timestamp', '<=', Timestamp.fromDate(options.endDate)));
      }

      // Add employee filter
      if (options.employeeId && options.employeeId !== 'all') {
        queryConstraints.push(where('uid', '==', options.employeeId));
      }

      // Add status filter
      if (options.status && options.status !== 'all') {
        queryConstraints.push(where('status', '==', options.status));
      }

      // Add type filter
      if (options.type && options.type !== 'all') {
        queryConstraints.push(where('type', '==', options.type));
      }

      // Add location filter
      if (options.location && options.location !== 'all') {
        const isWarehouse = options.location === 'warehouse';
        queryConstraints.push(where('isWarehouse', '==', isWarehouse));
      }

      // Add ordering and limit
      queryConstraints.push(orderBy('timestamp', 'desc'));
      if (options.limitCount) {
        queryConstraints.push(limit(options.limitCount));
      }

      // Create query with constraints
      attendanceQuery = query(attendanceQuery, ...queryConstraints);

      // Set up real-time listener
      const unsubscribe = onSnapshot(attendanceQuery, async (snapshot) => {
        console.log('📥 Received attendance records update:', snapshot.docs.length, 'records');

        const records = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const record = {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || null,
              createdAt: data.createdAt?.toDate() || null,
              updatedAt: data.updatedAt?.toDate() || null
            };

            // Resolve employee name
            record.employeeName = await this.resolveEmployeeName(record.uid);

            return record;
          })
        );

        callback(records);
      }, (error) => {
        console.error('❌ Attendance subscription error:', error);
        callback([]);
      });

      return unsubscribe;

    } catch (error) {
      console.error('❌ Error setting up attendance subscription:', error);
      callback([]);
      return () => {}; // Return empty unsubscribe function
    }
  }

  /**
   * Calculate total hours worked from attendance records
   * @param {Array} records - Attendance records
   * @returns {number} Total hours worked
   */
  calculateTotalHoursWorked(records) {
    try {
      console.log('⏰ Calculating total hours worked from', records.length, 'records');

      // Group records by employee and date
      const employeeDays = new Map();

      records.forEach(record => {
        if (!record.timestamp || !record.uid) return;

        const date = record.timestamp.toISOString().split('T')[0]; // YYYY-MM-DD
        const key = `${record.uid}_${date}`;

        if (!employeeDays.has(key)) {
          employeeDays.set(key, { clockIn: null, clockOut: null });
        }

        const dayRecord = employeeDays.get(key);
        
        if (record.type === 'clock-in') {
          // Use the latest clock-in for the day
          if (!dayRecord.clockIn || record.timestamp > dayRecord.clockIn.timestamp) {
            dayRecord.clockIn = record;
          }
        } else if (record.type === 'clock-out') {
          // Use the latest clock-out for the day
          if (!dayRecord.clockOut || record.timestamp > dayRecord.clockOut.timestamp) {
            dayRecord.clockOut = record;
          }
        }
      });

      // Calculate hours for each employee-day pair
      let totalHours = 0;
      let validPairs = 0;

      employeeDays.forEach((dayRecord, key) => {
        if (dayRecord.clockIn && dayRecord.clockOut) {
          const clockInTime = dayRecord.clockIn.timestamp.getTime();
          const clockOutTime = dayRecord.clockOut.timestamp.getTime();
          
          // Only count if clock-out is after clock-in
          if (clockOutTime > clockInTime) {
            const hoursWorked = (clockOutTime - clockInTime) / (1000 * 60 * 60); // Convert to hours
            totalHours += hoursWorked;
            validPairs++;
            
            console.log(`⏰ ${key}: ${hoursWorked.toFixed(2)} hours`);
          }
        }
      });

      console.log(`⏰ Total hours calculated: ${totalHours.toFixed(2)} hours from ${validPairs} valid pairs`);
      return Math.round(totalHours * 100) / 100; // Round to 2 decimal places

    } catch (error) {
      console.error('❌ Error calculating total hours worked:', error);
      return 0;
    }
  }

  /**
   * Get attendance statistics for the given date range and filters
   * @param {Object} options - Filter options
   * @returns {Promise<Object>} Statistics object
   */
  async getAttendanceStats(options = {}) {
    try {
      console.log('📊 Calculating attendance statistics with options:', options);

      // Build query for stats calculation
      let attendanceQuery = collection(db, 'attendance');
      const queryConstraints = [];

      // Add date range filters
      if (options.startDate) {
        queryConstraints.push(where('timestamp', '>=', Timestamp.fromDate(options.startDate)));
      }
      if (options.endDate) {
        queryConstraints.push(where('timestamp', '<=', Timestamp.fromDate(options.endDate)));
      }

      // Add employee filter
      if (options.employeeId && options.employeeId !== 'all') {
        queryConstraints.push(where('uid', '==', options.employeeId));
      }

      // Add status filter
      if (options.status && options.status !== 'all') {
        queryConstraints.push(where('status', '==', options.status));
      }

      // Add type filter
      if (options.type && options.type !== 'all') {
        queryConstraints.push(where('type', '==', options.type));
      }

      // Add location filter
      if (options.location && options.location !== 'all') {
        const isWarehouse = options.location === 'warehouse';
        queryConstraints.push(where('isWarehouse', '==', isWarehouse));
      }

      queryConstraints.push(orderBy('timestamp', 'desc'));
      attendanceQuery = query(attendanceQuery, ...queryConstraints);

      const snapshot = await getDocs(attendanceQuery);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null
      }));

      // Calculate total hours worked
      const totalHoursWorked = this.calculateTotalHoursWorked(records);

      // Calculate statistics
      const stats = {
        totalRecords: records.length,
        approvedRecords: records.filter(r => r.status === 'approved').length,
        pendingRecords: records.filter(r => r.status === 'pending').length,
        rejectedRecords: records.filter(r => r.status === 'rejected').length,
        clockIns: records.filter(r => r.type === 'clock-in').length,
        clockOuts: records.filter(r => r.type === 'clock-out').length,
        warehouseAttendance: records.filter(r => r.isWarehouse).length,
        offSiteAttendance: records.filter(r => !r.isWarehouse).length,
        uniqueEmployees: new Set(records.map(r => r.uid)).size,
        averageValidationScore: records.length > 0 
          ? Math.round((records.reduce((sum, r) => sum + (r.validationResults?.overallScore || 0), 0) / records.length) * 100) / 100
          : 0,
        totalHoursWorked: totalHoursWorked
      };

      console.log('📈 Calculated stats:', stats);
      return {
        success: true,
        stats: stats
      };

    } catch (error) {
      console.error('❌ Error calculating attendance stats:', error);
      return {
        success: false,
        error: error.message || 'Failed to calculate attendance statistics',
        stats: {
          totalRecords: 0,
          approvedRecords: 0,
          pendingRecords: 0,
          rejectedRecords: 0,
          clockIns: 0,
          clockOuts: 0,
          warehouseAttendance: 0,
          offSiteAttendance: 0,
          uniqueEmployees: 0,
          averageValidationScore: 0,
          totalHoursWorked: 0
        }
      };
    }
  }

  /**
   * Update attendance record status (approve/reject)
   * @param {string} attendanceId - Attendance record ID
   * @param {string} newStatus - New status (approved/pending/rejected)
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise<Object>} Result object
   */
  async updateAttendanceStatus(attendanceId, newStatus, adminNotes = '') {
    try {
      console.log('🔄 Updating attendance status:', { attendanceId, newStatus, adminNotes });

      if (!attendanceId) {
        throw new Error('Attendance ID is required');
      }

      if (!['approved', 'pending', 'rejected'].includes(newStatus)) {
        throw new Error('Invalid status. Must be approved, pending, or rejected');
      }

      const attendanceRef = doc(db, 'attendance', attendanceId);
      const updateData = {
        status: newStatus,
        updatedAt: Timestamp.now()
      };

      if (adminNotes) {
        updateData.adminNotes = adminNotes;
      }

      await updateDoc(attendanceRef, updateData);

      console.log('✅ Attendance status updated successfully');
      return {
        success: true,
        message: `Attendance record ${newStatus} successfully`
      };

    } catch (error) {
      console.error('❌ Error updating attendance status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update attendance status'
      };
    }
  }

  /**
   * Get attendance history for a specific employee
   * @param {string} employeeId - Employee UID
   * @param {Object} dateRange - Date range filter
   * @param {number} limitCount - Maximum number of records
   * @returns {Promise<Object>} Employee attendance history
   */
  async getEmployeeAttendanceHistory(employeeId, dateRange = {}, limitCount = 100) {
    try {
      console.log('📋 Getting employee attendance history:', { employeeId, dateRange, limitCount });

      if (!employeeId) {
        throw new Error('Employee ID is required');
      }

      let attendanceQuery = query(
        collection(db, 'attendance'),
        where('uid', '==', employeeId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      // Add date filters if provided
      const queryConstraints = [
        where('uid', '==', employeeId)
      ];

      if (dateRange.startDate) {
        queryConstraints.push(where('timestamp', '>=', Timestamp.fromDate(dateRange.startDate)));
      }
      if (dateRange.endDate) {
        queryConstraints.push(where('timestamp', '<=', Timestamp.fromDate(dateRange.endDate)));
      }

      queryConstraints.push(orderBy('timestamp', 'desc'));
      queryConstraints.push(limit(limitCount));

      attendanceQuery = query(collection(db, 'attendance'), ...queryConstraints);

      const snapshot = await getDocs(attendanceQuery);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null,
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      // Get employee name
      const employeeName = await this.resolveEmployeeName(employeeId);

      console.log('📊 Retrieved employee attendance history:', records.length, 'records');
      return {
        success: true,
        employeeName: employeeName,
        records: records,
        count: records.length
      };

    } catch (error) {
      console.error('❌ Error getting employee attendance history:', error);
      return {
        success: false,
        error: error.message || 'Failed to get employee attendance history',
        records: []
      };
    }
  }

  /**
   * Export attendance data for reporting
   * @param {Object} options - Filter options
   * @returns {Promise<Object>} Exported data
   */
  async exportAttendanceData(options = {}) {
    try {
      console.log('📤 Exporting attendance data with options:', options);

      // Get all records matching the filters
      const statsResult = await this.getAttendanceStats(options);
      if (!statsResult.success) {
        throw new Error(statsResult.error);
      }

      // Build query to get detailed records
      let attendanceQuery = collection(db, 'attendance');
      const queryConstraints = [];

      // Add filters (same as in getAttendanceStats)
      if (options.startDate) {
        queryConstraints.push(where('timestamp', '>=', Timestamp.fromDate(options.startDate)));
      }
      if (options.endDate) {
        queryConstraints.push(where('timestamp', '<=', Timestamp.fromDate(options.endDate)));
      }
      if (options.employeeId && options.employeeId !== 'all') {
        queryConstraints.push(where('uid', '==', options.employeeId));
      }
      if (options.status && options.status !== 'all') {
        queryConstraints.push(where('status', '==', options.status));
      }
      if (options.type && options.type !== 'all') {
        queryConstraints.push(where('type', '==', options.type));
      }
      if (options.location && options.location !== 'all') {
        const isWarehouse = options.location === 'warehouse';
        queryConstraints.push(where('isWarehouse', '==', isWarehouse));
      }

      queryConstraints.push(orderBy('timestamp', 'desc'));
      attendanceQuery = query(attendanceQuery, ...queryConstraints);

      const snapshot = await getDocs(attendanceQuery);
      const records = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();
          const record = {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || null,
            createdAt: data.createdAt?.toDate() || null,
            updatedAt: data.updatedAt?.toDate() || null
          };

          // Resolve employee name
          record.employeeName = await this.resolveEmployeeName(record.uid);

          return record;
        })
      );

      console.log('📊 Exported attendance data:', records.length, 'records');
      return {
        success: true,
        data: {
          records: records,
          stats: statsResult.stats,
          exportDate: new Date(),
          filters: options
        }
      };

    } catch (error) {
      console.error('❌ Error exporting attendance data:', error);
      return {
        success: false,
        error: error.message || 'Failed to export attendance data'
      };
    }
  }

  /**
   * Resolve employee name from UID using cache
   * @param {string} uid - Employee UID
   * @returns {Promise<string>} Employee name
   */
  async resolveEmployeeName(uid) {
    try {
      // Check cache first
      if (this.employeeCache.has(uid)) {
        return this.employeeCache.get(uid);
      }

      // Fetch employee data
      const employeesResult = await employeeService.getEmployees();
      if (employeesResult.success && employeesResult.employees) {
        // Update cache with all employees
        employeesResult.employees.forEach(employee => {
          this.employeeCache.set(employee.uid, employee.name || 'Unknown Employee');
        });

        // Return the requested employee name
        return this.employeeCache.get(uid) || 'Unknown Employee';
      }

      return 'Unknown Employee';

    } catch (error) {
      console.error('❌ Error resolving employee name:', error);
      return 'Unknown Employee';
    }
  }

  /**
   * Get secure signed URL for attendance photo viewing with enhanced retry logic
   * @param {string} attendanceId - Attendance record ID
   * @param {number} retryCount - Current retry attempt (internal use)
   * @returns {Promise<string>} Signed photo URL with 2-hour expiration
   */
  async getAttendancePhoto(attendanceId, retryCount = 0) {
    const maxRetries = 3;
    const retryDelay = Math.min(Math.pow(2, retryCount) * 1000, 8000); // Exponential backoff, max 8s
    const requestId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    try {
      console.log(`🔐 [${requestId}] Requesting signed URL for attendance: ${attendanceId} (attempt ${retryCount + 1}/${maxRetries + 1})`);

      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      // Call the getAttendancePhoto Cloud Function with enhanced timeout
      const getPhotoFunction = httpsCallable(functions, 'getAttendancePhoto');
      
      // Progressive timeout - longer for first attempt, shorter for retries
      const timeoutDuration = retryCount === 0 ? 25000 : 15000;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Request timeout after ${timeoutDuration}ms`)), timeoutDuration);
      });

      console.log(`⏱️ [${requestId}] Starting request with ${timeoutDuration}ms timeout...`);
      const startTime = Date.now();

      const result = await Promise.race([
        getPhotoFunction({ attendanceId }),
        timeoutPromise
      ]);

      const duration = Date.now() - startTime;
      console.log(`⏱️ [${requestId}] Request completed in ${duration}ms`);

    if (result.data && result.data.success) {
      console.log(`✅ [${requestId}] Signed URL generated successfully, expires at:`, result.data.expiresAt);
      console.log(`📊 [${requestId}] Server request ID:`, result.data.metadata?.requestId);
      console.log(`🔍 [${requestId}] Returned URL analysis:`, {
        signedUrl: result.data.signedUrl,
        isV4SignedUrl: result.data.signedUrl?.includes('X-Goog-Algorithm=') && result.data.signedUrl?.includes('X-Goog-Signature='),
        isPublicUrl: result.data.signedUrl?.includes('/v0/b/') && result.data.signedUrl?.includes('/o/'),
        startsWithStorage: result.data.signedUrl?.startsWith('https://storage.googleapis.com/'),
        method: result.data.method,
        urlLength: result.data.signedUrl?.length
      });
      return result.data.signedUrl;
    } else {
        const errorMessage = result.data?.message || 'Unknown error occurred';
        console.error(`❌ [${requestId}] Failed to get signed URL:`, errorMessage);
        
        // Create error object with proper code for better error handling
        const error = new Error(errorMessage);
        error.code = result.data?.code || 'functions/internal';
        error.serverRequestId = result.data?.metadata?.requestId;
        throw error;
      }

    } catch (error) {
      const duration = Date.now() - (Date.now() - (error.message.includes('timeout') ? 0 : 1000));
      console.error(`❌ [${requestId}] Error getting attendance photo (attempt ${retryCount + 1}) after ${duration}ms:`, error);
      
      // Enhanced error logging with more details
      this.logPhotoError(attendanceId, error, retryCount, requestId);
      
      // Log the specific error details for debugging
      console.error(`🔍 [${requestId}] Detailed error info:`, {
        errorName: error.name,
        errorCode: error.code,
        errorMessage: error.message,
        hasDetails: !!error.details,
        attendanceId: attendanceId,
        retryCount: retryCount,
        timestamp: new Date().toISOString(),
        serverRequestId: error.serverRequestId,
        clientRequestId: requestId
      });
      
      // Determine if we should retry
      const shouldRetry = this.shouldRetryPhotoRequest(error, retryCount, maxRetries);
      
      if (shouldRetry) {
        console.log(`🔄 [${requestId}] Retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.getAttendancePhoto(attendanceId, retryCount + 1);
      }
      
      // Re-throw the error with enhanced information
      throw this.enhancePhotoError(error, attendanceId, retryCount, requestId);
    }
  }

  /**
   * Determine if a photo request should be retried with enhanced logic
   * @param {Error} error - The error that occurred
   * @param {number} retryCount - Current retry count
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {boolean} Whether to retry the request
   */
  shouldRetryPhotoRequest(error, retryCount, maxRetries) {
    if (retryCount >= maxRetries) {
      console.log(`🚫 Max retries (${maxRetries}) reached, not retrying`);
      return false;
    }

    // Don't retry for certain error types
    const nonRetryableErrors = [
      'functions/permission-denied',
      'functions/not-found',
      'functions/unauthenticated',
      'functions/invalid-argument'
    ];

    if (nonRetryableErrors.includes(error.code)) {
      console.log(`🚫 Non-retryable error code: ${error.code}, not retrying`);
      return false;
    }

    // Retry for network errors, timeouts, and internal server errors
    const retryableErrors = [
      'functions/internal',
      'functions/unavailable',
      'functions/deadline-exceeded',
      'functions/cancelled'
    ];

    const isRetryable = retryableErrors.includes(error.code) || 
                       error.message.includes('timeout') ||
                       error.message.includes('network') ||
                       error.message.includes('fetch') ||
                       error.message.includes('INTERNAL') ||
                       error.message.includes('500') ||
                       error.message.includes('502') ||
                       error.message.includes('503') ||
                       error.message.includes('504');

    console.log(`🔄 Retry decision: ${isRetryable ? 'YES' : 'NO'} (error: ${error.code || error.message})`);
    return isRetryable;
  }

  /**
   * Enhance error with additional context and better user messages
   * @param {Error} error - Original error
   * @param {string} attendanceId - Attendance record ID
   * @param {number} retryCount - Number of retries attempted
   * @param {string} requestId - Client request ID
   * @returns {Error} Enhanced error
   */
  enhancePhotoError(error, attendanceId, retryCount, requestId) {
    const enhancedError = new Error(error.message);
    enhancedError.code = error.code;
    enhancedError.attendanceId = attendanceId;
    enhancedError.retryCount = retryCount;
    enhancedError.timestamp = new Date().toISOString();
    enhancedError.clientRequestId = requestId;
    enhancedError.serverRequestId = error.serverRequestId;
    
    // Add user-friendly messages with more context
    if (error.code === 'functions/permission-denied') {
      enhancedError.userMessage = 'Access denied. You do not have permission to view this photo.';
      enhancedError.actionable = 'Contact your administrator if you believe this is an error.';
    } else if (error.code === 'functions/not-found') {
      enhancedError.userMessage = 'Photo not found. The photo may have been deleted or moved.';
      enhancedError.actionable = 'Please refresh the page and try again.';
    } else if (error.code === 'functions/unauthenticated') {
      enhancedError.userMessage = 'Authentication required. Please log in again.';
      enhancedError.actionable = 'Please refresh the page and log in again.';
    } else if (error.code === 'functions/deadline-exceeded' || error.message.includes('timeout')) {
      enhancedError.userMessage = 'Request timed out. The server is taking too long to respond.';
      enhancedError.actionable = 'Please check your internet connection and try again.';
    } else if (error.code === 'functions/unavailable') {
      enhancedError.userMessage = 'Service temporarily unavailable. Please try again in a moment.';
      enhancedError.actionable = 'Wait a few seconds and try again.';
    } else if (error.code === 'functions/internal' || error.message.includes('INTERNAL')) {
      enhancedError.userMessage = 'Server error occurred while loading the photo.';
      enhancedError.actionable = 'Please try again. If the problem persists, contact support.';
    } else {
      enhancedError.userMessage = 'Unable to load the photo due to an unexpected error.';
      enhancedError.actionable = 'Please try again or contact support if the problem continues.';
    }

    return enhancedError;
  }

  /**
   * Enhanced photo error logging for debugging and monitoring
   * @param {string} attendanceId - Attendance record ID
   * @param {Error} error - The error that occurred
   * @param {number} retryCount - Current retry count
   * @param {string} requestId - Client request ID
   */
  logPhotoError(attendanceId, error, retryCount, requestId) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      attendanceId: attendanceId,
      errorCode: error.code,
      errorMessage: error.message,
      errorName: error.name,
      retryCount: retryCount,
      clientRequestId: requestId,
      serverRequestId: error.serverRequestId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      connectionType: navigator.connection?.effectiveType || 'unknown',
      onLine: navigator.onLine,
      timestamp_ms: Date.now()
    };

    // Log to console for development
    console.error('📊 Photo Error Log:', errorLog);

    // Store error in session storage for debugging
    try {
      const existingErrors = JSON.parse(sessionStorage.getItem('photoErrors') || '[]');
      existingErrors.push(errorLog);
      // Keep only last 10 errors
      if (existingErrors.length > 10) {
        existingErrors.splice(0, existingErrors.length - 10);
      }
      sessionStorage.setItem('photoErrors', JSON.stringify(existingErrors));
    } catch (storageError) {
      console.warn('Failed to store error log in session storage:', storageError);
    }

    // In production, you might want to send this to a logging service
    // Example: analytics.track('photo_load_error', errorLog);
  }

  /**
   * Get photo with enhanced fallback mechanisms
   * @param {string} attendanceId - Attendance record ID
   * @param {string} photoUrl - Original photo URL from database
   * @returns {Promise<string>} Accessible photo URL
   */
  async getPhotoWithFallback(attendanceId, photoUrl) {
    const fallbackId = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    console.log(`🔄 [${fallbackId}] Starting photo retrieval with fallback for attendance: ${attendanceId}`);

    // Method 1: Try signed URL first
    try {
      console.log(`🔐 [${fallbackId}] Attempting signed URL method...`);
      const signedUrl = await this.getAttendancePhoto(attendanceId);
      if (signedUrl) {
        console.log(`✅ [${fallbackId}] Signed URL method successful`);
        return signedUrl;
      }
    } catch (signedUrlError) {
      console.log(`⚠️ [${fallbackId}] Signed URL method failed:`, signedUrlError.message);
      
      // If it's a non-retryable error, don't try fallbacks
      if (['functions/permission-denied', 'functions/not-found', 'functions/unauthenticated'].includes(signedUrlError.code)) {
        console.log(`🚫 [${fallbackId}] Non-retryable error, not attempting fallbacks`);
        throw signedUrlError;
      }
    }

    // Method 2: Try direct Firebase Storage URL
    try {
      console.log(`🌐 [${fallbackId}] Attempting direct URL fallback...`);
      let directUrl = photoUrl;
      
      if (photoUrl.startsWith('gs://')) {
        // Convert gs:// URL to https:// URL with proper encoding
        const gsUrl = photoUrl.replace('gs://', '');
        const [bucket, ...pathParts] = gsUrl.split('/');
        const encodedPath = pathParts.map(part => encodeURIComponent(part)).join('%2F');
        directUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
        console.log(`🔗 [${fallbackId}] Converted gs:// to direct URL`);
      }

      // Test if the direct URL is accessible with timeout
      console.log(`🔍 [${fallbackId}] Testing direct URL accessibility...`);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(directUrl, { 
          method: 'HEAD',
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          console.log(`✅ [${fallbackId}] Direct URL accessible, using fallback`);
          return directUrl;
        } else {
          throw new Error(`Direct URL returned status: ${response.status} ${response.statusText}`);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Direct URL accessibility check timed out');
        }
        throw fetchError;
      }

    } catch (directUrlError) {
      console.error(`❌ [${fallbackId}] Direct URL fallback failed:`, directUrlError.message);
    }

    // Method 3: Try alternative Firebase Storage URL format
    try {
      console.log(`🔄 [${fallbackId}] Attempting alternative URL format...`);
      if (photoUrl.startsWith('gs://')) {
        const gsUrl = photoUrl.replace('gs://', '');
        const [bucket, ...pathParts] = gsUrl.split('/');
        const altUrl = `https://storage.googleapis.com/${bucket}/${pathParts.join('/')}`;
        
        console.log(`🔍 [${fallbackId}] Testing alternative URL format...`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        try {
          const response = await fetch(altUrl, { 
            method: 'HEAD',
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          if (response.ok) {
            console.log(`✅ [${fallbackId}] Alternative URL format accessible`);
            return altUrl;
          }
        } catch (altFetchError) {
          clearTimeout(timeoutId);
          console.log(`⚠️ [${fallbackId}] Alternative URL format failed:`, altFetchError.message);
        }
      }
    } catch (altUrlError) {
      console.error(`❌ [${fallbackId}] Alternative URL format failed:`, altUrlError.message);
    }

    // All methods failed
    console.error(`❌ [${fallbackId}] All photo retrieval methods failed`);
    const finalError = new Error('Photo is not accessible through any method');
    finalError.code = 'photo-unavailable';
    finalError.attendanceId = attendanceId;
    finalError.originalUrl = photoUrl;
    finalError.fallbackId = fallbackId;
    throw finalError;
  }

  /**
   * Clear employee cache (useful for refreshing employee data)
   */
  clearEmployeeCache() {
    this.employeeCache.clear();
    console.log('🗑️ Employee cache cleared');
  }

  /**
   * Get all employees for filter dropdown
   * @returns {Promise<Array>} List of employees
   */
  async getEmployeesForFilter() {
    try {
      const result = await employeeService.getEmployees();
      if (result.success) {
        // Update cache
        result.employees.forEach(employee => {
          this.employeeCache.set(employee.uid, employee.name || 'Unknown Employee');
        });

        return result.employees.map(employee => ({
          uid: employee.uid,
          name: employee.name || 'Unknown Employee',
          employeeId: employee.employeeId || 'N/A',
          isActive: employee.isActive !== false
        }));
      }

      return [];

    } catch (error) {
      console.error('❌ Error getting employees for filter:', error);
      return [];
    }
  }
}

// Create and export singleton instance
const adminAttendanceService = new AdminAttendanceService();
export default adminAttendanceService;
